import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, GitBranch, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useEntities, useEvents, useTimelines, useJumps, useInvalidate, useQuotes, useTimelineCalDates } from "@/lib/queries";
import { CalendarsTab } from "@/components/calendar/CalendarsTab";
import { quotesByEvent } from "@/lib/quoteLinks";
import { JumpDialog } from "@/components/JumpDialog";
import { PageHeader } from "@/components/CategoryBadge";
import { TimelineTrack } from "@/components/TimelineTrack";
import { TimeScrubber } from "@/components/TimeScrubber";
import { BranchTree } from "@/components/BranchTree";
import { TimelineDialog } from "@/components/TimelineDialog";
import { EventDialog } from "@/components/EventDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { LifeDatesSync } from "@/components/LifeDatesSync";
import { evDate } from "@/lib/eventDate";
import { fmtT, tlFormat } from "@/lib/calFormat";

function EventList({ events, onClick, names, calDates = [] }) {
  return (
    <div className="divide-y divide-[#2D3442] rounded-md border border-[#2D3442]" data-testid="event-list">
      {events.map((e) => (
        <button key={e.id} onClick={() => onClick(e)} data-testid={`event-row-${e.id}`}
          className={`w-full grid grid-cols-[150px_1fr_auto] gap-4 px-4 py-3 text-left hover:bg-[#13171F] transition-colors ${e.inherited ? "opacity-50" : ""}`}>
          <span className="text-xs text-[#F59E0B] pt-1" data-testid={`event-date-${e.id}`}>{evDate(e)}</span>
          <span>
            <span className="block text-[#F3EBDD]">{e.title}</span>
            <span className="block text-xs text-[#A8A090]">{[e.date_label && e.display_date !== e.date_label ? e.display_date : null, names[e.location_entity_id], e.entity_ids.map((id) => names[id]).filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</span>
            {calDates.slice(1).map((c) => c.dates[e.id] && (
              <span key={c.calendar_id} className="mr-3 inline-flex items-center gap-1.5 text-xs" style={{ color: c.color }} data-testid={`event-cal-date-${e.id}-${c.calendar_id}`}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: c.color }} />{c.dates[e.id]}
              </span>
            ))}
          </span>
          {e.inherited && <span className="text-[10px] uppercase tracking-widest text-[#8B5CF6] pt-1">inherited</span>}
        </button>
      ))}
    </div>
  );
}

export default function TimelinesPage() {
  const { universe, canEdit, openEntity, base } = useOutletContext();
  const invalidate = useInvalidate();
  const { data: timelines = [] } = useTimelines(universe.id);
  const { data: entities = [] } = useEntities(universe.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusTid = searchParams.get("timeline");
  const focusT = searchParams.get("t") != null ? parseInt(searchParams.get("t"), 10) : null;
  const [tid, setTid] = useState(focusTid || "");
  const [tab, setTab] = useState(searchParams.get("tab") || "track");
  const [tlDialog, setTlDialog] = useState(null);
  const [evDialog, setEvDialog] = useState(null);
  const [delTl, setDelTl] = useState(false);
  const current = timelines.find((t) => t.id === tid) || timelines[0];
  const fmt = tlFormat(timelines, current);
  const { data: events = [] } = useEvents(current?.id);
  const { data: allJumps = [] } = useJumps(universe.id);
  const { data: allQuotes = [] } = useQuotes(universe.id);
  const evQuotes = quotesByEvent(allQuotes);
  const { data: calDates = [] } = useTimelineCalDates(current?.id);
  const jumps = allJumps.filter((j) => [j.departure?.timeline_id, j.arrival?.timeline_id].includes(current?.id));
  const [jumpFrom, setJumpFrom] = useState(null);
  const goTo = (side) => { setTid(side.timeline_id); setSearchParams({ timeline: side.timeline_id, t: side.timestamp_int }); };
  const removeJump = async (id) => { await api.delete(`/jumps/${id}`); invalidate(["jumps", universe.id], "snapshot"); };
  const names = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e.name])), [entities]);
  const range = useMemo(() => {
    const ts = events.map((e) => e.timestamp_int);
    if (!ts.length) return [0, 100];
    const lo = Math.min(...ts), hi = Math.max(...ts);
    return lo === hi ? [lo - 10, hi + 10] : [lo, hi];
  }, [events]);
  const [scrubT, setScrubT] = useState(null);
  useEffect(() => { setScrubT(focusT != null && current?.id === focusTid ? focusT : range[1]); }, [current?.id, range, focusT, focusTid]);

  const refresh = () => invalidate(["events"], ["timelines", universe.id], "snapshot", "entity", ["jumps", universe.id], ["cal"]);
  const removeTl = async () => {
    try {
      await api.delete(`/timelines/${current.id}`);
      setTid("");
      refresh();
      toast.success("Timeline erased");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDelTl(false);
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-10">
      <PageHeader eyebrow="Chronicle" title="Timelines">
        {canEdit && (
          <>
            {universe.features?.includes("timelines") && <LifeDatesSync uid={universe.id} onDone={refresh} />}
            <Button variant="ghost" onClick={() => setTlDialog({ defaultParent: current?.id })} data-testid="new-timeline-button"><GitBranch className="h-4 w-4 mr-1.5" />New timeline</Button>
            <Button onClick={() => setEvDialog({})} disabled={!current} data-testid="add-event-button"><Plus className="h-4 w-4 mr-1.5" />Add event</Button>
          </>
        )}
      </PageHeader>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={current?.id || ""} onValueChange={setTid}>
          <SelectTrigger className="w-72 h-11 font-display text-lg" data-testid="timeline-select"><SelectValue placeholder="Select timeline" /></SelectTrigger>
          <SelectContent>{timelines.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
        {current && canEdit && (
          <>
            <Button size="icon" variant="ghost" onClick={() => setTlDialog({ initial: current })} data-testid="edit-timeline-button"><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setDelTl(true)} data-testid="delete-timeline-button"><Trash2 className="h-4 w-4 text-red-400" /></Button>
          </>
        )}
        {current?.parent_timeline_id && <span className="text-sm text-[#8B5CF6]" data-testid="timeline-branch-info">Branch of {timelines.find((t) => t.id === current.parent_timeline_id)?.name} · diverges at {fmtT(fmt, current.branch_point_timestamp)}</span>}
      </div>
      {current?.description && <p className="-mt-2 mb-6 text-sm text-[#A8A090] italic">{current.description}</p>}
      {current && events.length > 0 && (fmt
        ? <p className="-mt-2 mb-6 text-xs text-[#A8A090]" data-testid="timeline-date-format">Dates shown in <b style={{ color: fmt.color }}>{fmt.name}</b></p>
        : <p className="-mt-2 mb-6 text-xs text-[#A8A090]" data-testid="timeline-date-hint">Dates without a calendar show as years.{canEdit && <> <button onClick={() => setTab("calendars")} className="text-[#F59E0B] underline-offset-2 hover:underline" data-testid="timeline-link-calendar">Link a calendar to this timeline</button> to show full dates like “May 1st, 1980”.</>}</p>)}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5 bg-[#13171F] border border-[#2D3442]">
          <TabsTrigger value="track" data-testid="tab-track">Track</TabsTrigger>
          <TabsTrigger value="tree" data-testid="tab-tree">Branch tree</TabsTrigger>
          <TabsTrigger value="calendars" data-testid="tab-calendars">Calendars</TabsTrigger>
        </TabsList>
        <TabsContent value="track" className="space-y-6">
          {events.length === 0 ? (
            <div className="rounded-md border border-dashed border-[#2D3442] p-14 text-center" data-testid="timeline-empty">
              <p className="font-display text-3xl">No events recorded.</p>
              <p className="mt-2 text-sm text-[#A8A090]">Add an event, or process a dated note with the AI scribe.</p>
            </div>
          ) : (
            <>
              <TimelineTrack fmt={fmt} events={events} branchPoint={current?.branch_point_timestamp} scrubT={scrubT} onEventClick={setEvDialog} jumps={jumps} timelineId={current?.id} onJumpMarker={goTo} evQuotes={evQuotes} />
              <TimeScrubber fmt={fmt} timelineId={current.id} min={range[0]} max={range[1]} value={scrubT ?? range[1]} onChange={setScrubT} onOpenEntity={openEntity}
                mapLink={universe.modules?.maps !== false ? `${base}/maps?timeline=${current.id}&t=${scrubT ?? range[1]}` : null} />
              <EventList events={events} onClick={setEvDialog} names={names} calDates={calDates} />
              {jumps.length > 0 && (
                <section data-testid="jump-list">
                  <div className="lf-label mb-3 text-[#C4B5FD]">⧗ Time-travel jumps</div>
                  <div className="space-y-2">
                    {jumps.map((j) => (
                      <div key={j.id} className="flex items-start justify-between gap-3 rounded-md border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 px-4 py-3" data-testid={`jump-row-${j.id}`}>
                        <div className="text-sm">
                          <div className="text-[#F3EBDD]"><b className="font-display text-lg font-normal">{j.traveller_name}</b> · {j.departure?.title} <span className="text-[#A8A090]">({j.departure?.timeline_name})</span> → {j.arrival?.title} <span className="text-[#A8A090]">({j.arrival?.timeline_name})</span></div>
                          <div className="mt-1 text-[#C4B5FD]" data-testid={`jump-summary-${j.id}`}>{j.summary}</div>
                          {j.description && <div className="text-xs italic text-[#A8A090]">{j.description}</div>}
                        </div>
                        <div className="flex shrink-0 gap-1">
                          {j.arrival && j.arrival.timeline_id !== current?.id && <Button size="sm" variant="ghost" onClick={() => goTo(j.arrival)} data-testid={`jump-goto-${j.id}`}>Go to arrival</Button>}
                          {j.departure && j.departure.timeline_id !== current?.id && <Button size="sm" variant="ghost" onClick={() => goTo(j.departure)} data-testid={`jump-goto-dep-${j.id}`}>Go to departure</Button>}
                          {canEdit && <Button size="icon" variant="ghost" onClick={() => removeJump(j.id)} data-testid={`jump-delete-${j.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </TabsContent>
        <TabsContent value="calendars">
          <CalendarsTab universe={universe} canEdit={canEdit} entities={entities} timelines={timelines}
            onAddTimelineEvent={(p) => { setTid(p.timeline_id); setEvDialog({ prefill: true, timestamp_int: p.timestamp_int, display_date: p.display_date }); }}
            onOpenTimelineEvent={(id, t) => { setTid(id); setSearchParams({ timeline: id, t }); setTab("track"); }} />
        </TabsContent>
        <TabsContent value="tree">
          <BranchTree timelines={timelines} jumps={allJumps} activeId={current?.id} onSelect={(id) => { setTid(id); setTab("track"); }} />
        </TabsContent>
      </Tabs>
      {tlDialog && (
        <TimelineDialog open onOpenChange={(o) => !o && setTlDialog(null)} uid={universe.id} timelines={timelines} initial={tlDialog.initial} defaultParent={tlDialog.defaultParent}
          onSaved={(t) => { refresh(); setTid(t.id); toast.success("Timeline saved"); }} />
      )}
      {evDialog && (
        <EventDialog open onOpenChange={(o) => !o && setEvDialog(null)} uid={universe.id} timelineId={current.id} initial={evDialog.id || evDialog.prefill ? evDialog : null}
          entities={entities} onSaved={refresh} readOnly={!canEdit || evDialog.inherited} onAddJump={canEdit ? setJumpFrom : null}
          quotes={evDialog.id ? evQuotes[evDialog.id] || [] : []} onNavigate={(id) => { setEvDialog(null); openEntity(id); }} />
      )}
      {jumpFrom && (
        <JumpDialog open onOpenChange={(o) => !o && setJumpFrom(null)} uid={universe.id} departure={jumpFrom} timelines={timelines} entities={entities} onSaved={refresh} />
      )}
      <ConfirmDialog open={delTl} onOpenChange={setDelTl} onConfirm={removeTl} title={`Delete ${current?.name}?`} description="All events on this timeline will be erased. Branches must be deleted first." />
    </div>
  );
}
